import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Stack,
  TextProps,
  Accordion,
  Divider,
  Radio,
  Title,
  Button,
  ButtonProps,
  Text,
  HoverCard,
  Group,
  ThemeIcon,
  Skeleton,
} from "@mantine/core";
import { IconBookmark, IconBriefcase, IconHelpCircle, IconSparkles } from "@tabler/icons-react";
import BenchmarkingDashboardCartaLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-carta.png";
import BenchmarkingDashboardComprehensiveLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-comprehensive.png";
import BenchmarkingDashboardComptryxLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-comptryx.png";
import BenchmarkingDashboardMercerLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-mercer.png";
import BenchmarkingDashboardSalaryLogo from "app/lib/frontend/cm/pages/benchmarking-dashboard/_assets/benchmarking-dashboard-salary.png";
import _ from "lodash";
import Image from "next/image";
import Link from "next/link";

import { usePaidFeatureModalDisclosure, PaidFeatureModal } from "components/PaidFeatureModal";
import { mercerOptionLabelMap } from "constants/mercer-definitions";
import { useMercerSearchRouteInfo } from "hooks/routing";
import { useAuthorizedDataSets } from "hooks/useAuthorizedDataSets";
import { useSafeCrossAppUser } from "hooks/useSafeCrossAppUser";
import { isCompanyAdmin } from "lib/shared/permissions";
import {
  CompanySizeData,
  LocationHierarchyData,
  SectorHierarchyData,
  TierData,
} from "services/mercer/MercerDataService";

import { FilterBySearchString, MercerComboBox, SharedComboboxProps } from "./SearchByBar";

interface FilterSidebarProps {
  locations: LocationHierarchyData[];
  sectors: SectorHierarchyData[];
  companySizes: CompanySizeData[];
  tiers: TierData[];
  activeDataset: "comprehensive" | "mercer" | "comptryx";
}

const allDimensions = ["employeeCount", "revenue", "city", "sector", "state", "region", "tier"];

const DatasetDimensionsKeyMap = {
  comptryx: {
    employeeCount: "employeeCount",
    revenue: "revenue",
    city: "city",
    sector: "sector",
    state: "state",
    region: "region",
    tier: "tier",
  },
  mercer: {
    employeeCount: "employeeCount",
    revenue: "revenue",
    city: "city",
    sector: "sector",
    state: "state",
    region: "region",
    tier: "tier",
  },
  comprehensive: {
    employeeCount: "employeeCount",
    revenue: "revenue",
    city: "city",
    sector: "sector",
    state: "state",
    region: "region",
    tier: "tier",
  },
};

const getDimensionsToClear = (
  currentDimension: string,
  currentlyAppliedDimensions: string[],
  activeDataset: "comptryx" | "mercer" | "comprehensive",
) => {
  const geoDimensions = ["city", "state", "region", "tier"];
  const companyTypeDimensions = ["sector", "employeeCount", "revenue"];
  const allDimensions = geoDimensions.concat(companyTypeDimensions);

  if (activeDataset === "mercer") {
    return allDimensions;
  }

  if (geoDimensions.includes(currentDimension)) {
    return geoDimensions;
  }

  if (companyTypeDimensions.includes(currentDimension)) {
    return companyTypeDimensions;
  }
};

const SidebarSectionText = Text.withProps({
  fw: 400,
  size: "xs",
  tt: "uppercase",
  p: 8,
});

const iconMr = 2;

const navLinkProps: ButtonProps = {
  fullWidth: true,
  justify: "left",
  variant: "subtle",
  color: "gray",
};

export const DataSetSection = (props: {
  activeDataset: "comprehensive" | "mercer" | "comptryx" | "salary" | "carta";
}) => {
  const { activeDataset } = props;
  const authorizedDataSets = useAuthorizedDataSets();
  const [paidFeatureModalState, paidFeatureModalHandlers] = usePaidFeatureModalDisclosure();

  return (
    <Box mt={16}>
      <SidebarSectionText>Datasets</SidebarSectionText>
      <Box px={0} pb={0} pt={4}>
        <Button
          component={Link}
          href="/benchmarking/s"
          bg={activeDataset === "comprehensive" ? "gray.2" : undefined}
          // @ts-expect-error TS2322
          active={activeDataset === "comprehensive"}
          leftSection={
            <Box mr={iconMr}>
              <Image
                src={BenchmarkingDashboardComprehensiveLogo}
                alt={"Comprehensive"}
                height={16}
                width={16}
              />
            </Box>
          }
          {...navLinkProps}
        >
          Comprehensive
        </Button>

        <Button
          component={Link}
          href="/benchmarking/mercer"
          bg={activeDataset === "mercer" ? "gray.2" : undefined}
          // @ts-expect-error TS2322
          active={activeDataset === "mercer"}
          leftSection={
            <Box mr={iconMr}>
              <Image src={BenchmarkingDashboardMercerLogo} alt={"Mercer"} height={16} width={16} />
            </Box>
          }
          {...navLinkProps}
          onClick={(e) => {
            if (!authorizedDataSets.mercer) {
              e.preventDefault();
              paidFeatureModalHandlers.open("benchmarking-mercer");
            }
          }}
        >
          Mercer
        </Button>

        <Button
          component={Link}
          bg={activeDataset === "comptryx" ? "gray.2" : undefined}
          href="/benchmarking/comptryx"
          // @ts-expect-error TS2322
          active={activeDataset === "comptryx"}
          leftSection={
            <Box mr={iconMr}>
              <Image
                src={BenchmarkingDashboardComptryxLogo}
                alt={"Comptryx"}
                height={16}
                width={16}
              />
            </Box>
          }
          onClick={(e) => {
            if (!authorizedDataSets.comptryx) {
              e.preventDefault();
              paidFeatureModalHandlers.open("benchmarking-comptryx");
            }
          }}
          {...navLinkProps}
        >
          Comptryx
        </Button>

        {authorizedDataSets.carta && (
          <Button
            component={Link}
            bg={activeDataset === "carta" ? "gray.2" : undefined}
            href="/benchmarking/carta"
            // @ts-expect-error TS2322
            active={activeDataset === "carta"}
            leftSection={
              <Box mr={iconMr}>
                <Image src={BenchmarkingDashboardCartaLogo} alt={"carta"} height={16} width={16} />
              </Box>
            }
            {...navLinkProps}
          >
            Carta
          </Button>
        )}

        {authorizedDataSets.salary && (
          <Button
            component={Link}
            bg={activeDataset === "salary" ? "gray.2" : undefined}
            href="/benchmarking/salary"
            // @ts-expect-error TS2322
            active={activeDataset === "salary"}
            leftSection={
              <Box mr={iconMr}>
                <Image
                  src={BenchmarkingDashboardSalaryLogo}
                  alt={"salary.com"}
                  height={16}
                  width={16}
                />
              </Box>
            }
            {...navLinkProps}
          >
            Salary.com
          </Button>
        )}
      </Box>
      <PaidFeatureModal {...paidFeatureModalState} onClose={paidFeatureModalHandlers.close} />
    </Box>
  );
};

export function ToolsSection() {
  const user = useSafeCrossAppUser();

  if (!isCompanyAdmin(user)) {
    return null;
  }

  return (
    <Box>
      <SidebarSectionText>Tools</SidebarSectionText>
      <Button
        component="a"
        href="/benchmarking/saved-benchmarks"
        leftSection={
          <ThemeIcon mr={iconMr} variant="transparent" color="gray" size="xs">
            <IconBookmark size={16} />
          </ThemeIcon>
        }
        {...navLinkProps}
      >
        Saved benchmarks
      </Button>
      <Button
        component="a"
        href="/benchmarking/internal-jobs"
        leftSection={
          <ThemeIcon mr={iconMr} variant="transparent" color="gray" size="xs">
            <IconBriefcase size={16} />
          </ThemeIcon>
        }
        {...navLinkProps}
      >
        Custom jobs
      </Button>
      {user?.company?.enableAdminComptryxBenchmarking && (
        <Button
          component="a"
          href="/company/employees/benchmarks"
          leftSection={
            <ThemeIcon mr={iconMr} variant="transparent" color="gray" size="xs">
              <IconSparkles size={16} />
            </ThemeIcon>
          }
          {...navLinkProps}
        >
          Benchmark employees
        </Button>
      )}
    </Box>
  );
}

export function FilterSidebar({
  sectors,
  locations,
  companySizes,
  tiers,
  activeDataset,
}: FilterSidebarProps): JSX.Element {
  const { baseRoute, query, replaceQuery, queryString } = useMercerSearchRouteInfo();
  const [loadingDimensions, setLoadingDimensions] = useState(false);

  // @ts-expect-error TS2769
  const companySizeAndTierCountData = companySizes.concat(tiers);

  const nextRouteData = useRef(_.clone(query));

  // Initialize based on which filter is actually present in the query
  const getInitialCompanyTypeFilter = (): "employeeCount" | "revenue" | "sector" => {
    if (query.employeeCount) return "employeeCount";
    if (query.revenue) return "revenue";

    return "sector"; // Default to sector
  };

  const [currentCompanyTypeFilter, setCurrentCompanyTypeFilter] = useState<
    "employeeCount" | "revenue" | "sector"
  >(getInitialCompanyTypeFilter());

  // @ts-expect-error TS7053
  const allAppliedDimensions = allDimensions.filter((d) => query[d]);

  const currentlyAppliedGeoFilter =
    allAppliedDimensions.includes("city") || allAppliedDimensions.includes("state")
      ? "location"
      : "tier";

  const [currentGeoFilter, setCurrentGeoFilter] = useState<"tier" | "location">(
    currentlyAppliedGeoFilter,
  );

  const [dimensions, setDimensions] = useState<
    { name: string; optionValues: { value: string; label: string }[] }[]
  >([]);

  const optionLabelMap = mercerOptionLabelMap;

  const optionSortIndexMap = {
    "<100": 1,
    "100 to 249": 2,
    "250 to 499": 3,
    "500 to 999": 4,
    "1000 to 2499": 5,
    "2500+": 6,
    "<$100M": 1,
    "$100M to $199M": 2,
    "$200M to $399M": 3,
    "$400M to $699M": 4,
    "$700M to $999M": 5,
    "$1B to $1.99B": 6,
    "$2B+": 7,
    "Tier 1": 1,
    "Tier 2": 2,
    "Tier 3": 3,
    "Tier 4": 4,
    "$10B +": 8,
    "$2B to < $10B": 7,
    "$500M to < $2B": 6,
    "< $500M": 5,
  };

  const locationLabelMap: Record<string, string> = {
    "AZ - Phoenix": "AZ - Phoenix",
    "AZ - Tuscon": "AZ - Tuscon",
    "CA - LA/Orange Cnty": "CA - Los Angeles/Orange County",
    "CA - Sacramento": "CA - Sacramento",
    "CA - San Diego": "CA - San Diego",
    "CA - Slc Vly/Bay Area": "CA - Silicon Valley/San Francisco Bay Area",
    "CO - Colo Springs": "CO - Colorado Springs",
    "CO - Denver/Fort Collins": "CO - Denver/Fort Collins",
    "DC - D.C./Baltimore": "DC - Washington D.C./Baltimore",
    "FL - Miami": "FL - Miami",
    "FL - Orlando/Tampa": "FL - Orlando/Tampa",
    "GA - Atlanta": "GA - Atlanta",
    "HI - Hawaii": "HI - Hawaii",
    "ID - Boise": "ID - Boise",
    "IL - Chicago": "IL - Chicago",
    "KY - L'ville / Lexg": "KY - Louisville/Lexington",
    "MA - Boston/Pvd": "MA - Boston/Providence",
    "MI - Detroit": "MI - Detroit",
    "MN - Minn/St Paul": "MN - Minneapolis/St. Paul",
    "MO - St. Louis": "MO - St. Louis",
    "NC - Charlotte": "NC - Charlotte",
    "NC - RTP": "NC - Raleigh/Research Triangle Park",
    "NY - Tri State Metro": "NY - New York City/Tri State Metro",
    "OH - Columb/Clev": "OH - Columbus/Cleveland",
    "OR - Portland/Bvtn": "OR - Portland/Beaverton",
    "PA - Philly": "PA - Philadelphia",
    "PA - Pittsburgh": "PA - Pittsburgh",
    "TN - Nashville": "TN - Nashville",
    "TX - Austin": "TX - Austin",
    "TX - Dallas/Ft Worth": "TX - Dallas/Fort Worth",
    "TX - Houston": "TX - Houston",
    "TX - San Antonio": "TX - San Antonio",
    "UT - Salt Lake": "UT - Salt Lake City",
    "WA - Seattle/Tcma": "WA - Seattle/Tacoma",
    "WI - Milw / Mdsn": "WI - Milwaukee/Madison",
  };

  async function fetchDimensions() {
    return fetch(`/api/benchmarking/mercer/dimensions/${activeDataset}`)
      .then((res) => res.json())
      .then((data) => {
        const groupedDimensions = _.omit(_.groupBy(data, "dimensionType"), "summary");
        const dimensionNames = Object.keys(groupedDimensions);
        const dimensionObjects = _.sortBy(
          dimensionNames.map((name) => {
            const unfilteredOptionValues = _.sortBy(
              groupedDimensions[name].map((dimension) => {
                const label = optionLabelMap[dimension.dimensionValue] ?? dimension.dimensionValue;
                return {
                  value: dimension.dimensionValue,
                  label: label,
                };
              }),
              // @ts-expect-error TS7053
              (option) => optionSortIndexMap[option.label] ?? option.label,
            );

            const optionValues = unfilteredOptionValues.filter((option) => option.label !== "null");

            return {
              name,
              optionValues,
            };
          }),
          "name",
        );

        const tierDimensionObject = {
          name: "tier",
          optionValues: [
            {
              value: "1",
              label: "US Tier 1",
            },
            {
              value: "2",
              label: "US Tier 2",
            },
            {
              value: "3",
              label: "US Tier 3",
            },
            {
              value: "4",
              label: "US Tier 4",
            },
          ],
        };

        const allDimensionObjects = dimensionObjects.concat([tierDimensionObject]);

        setDimensions(allDimensionObjects);
      });
  }

  useEffect(() => {
    let active = true;
    setLoadingDimensions(true);
    load();

    return () => {
      active = false;
      setLoadingDimensions(false);
    };

    async function load() {
      if (!active) {
        return;
      }

      await fetchDimensions();
      setLoadingDimensions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    nextRouteData.current = _.clone(query);
  }, [queryString, query]);

  function renderIndividualFilter(
    dimension: { name: string; optionValues: { value: string; label: string }[] } | undefined,
    placeholder?: string,
  ) {
    if (!dimension) {
      return null;
    }

    if (!dimensions.length) {
      return null;
    }

    return (
      // @ts-expect-error TS2322
      <Stack key={dimension.name} spacing={4}>
        <MercerComboBox
          comboboxProps={{ ...SharedComboboxProps, width: 400 }}
          maxDropdownHeight={600}
          withinPortal
          filter={FilterBySearchString}
          clearable
          searchable
          // @ts-expect-error TS2322
          data={dimension.optionValues.map((option: { value: string }) => ({
            ...option,
            parent: "All",
            data_point_count:
              companySizeAndTierCountData?.find(
                // @ts-expect-error TS2339
                (value) => value.dimension_value === option.value || value.geo === option.value,
              )?.data_point_count || 0,
          }))}
          placeholder={`${placeholder || _.startCase(dimension.name)}`}
          value={(query as any)[dimension.name] || undefined}
          onChange={(value) => {
            const currentlyAppliedDimensions = allDimensions.filter(
              (d) => (nextRouteData.current as any)[d],
            );

            const dimensionsToClear = getDimensionsToClear(
              dimension.name,
              currentlyAppliedDimensions,
              activeDataset,
            );

            allDimensions.map((d) => {
              if (dimensionsToClear?.includes(d)) {
                // @ts-expect-error TS7053
                nextRouteData.current[d] = undefined;
              }
            });

            // @ts-expect-error TS7053
            nextRouteData.current[dimension.name] = value;
            replaceQuery(nextRouteData.current);
          }}
        />
      </Stack>
    );
  }

  const unsortedStates = locations
    .filter((location) => location.metro === "All")
    .map((location) => ({
      name: location.state,
      data_point_count: location.data_point_count,
    }));

  const states = _.sortBy(unsortedStates, (state) => {
    if (state.name === "United States" || state.name === "US") {
      return "A";
    }
    return state.name;
  });

  const hierarchicalLocations = states.map((state) => {
    const unsortedStateChildren = locations
      .filter((location) => location.state === state.name && location.metro !== "All")
      .map((location) => ({
        type: "city",
        name: location.metro,
        data_point_count: location.data_point_count,
      }));

    const stateChildren = _.sortBy(unsortedStateChildren, (city) => {
      if (city.name === "CA - California - Other") {
        return "CA - Z";
      }
      return city.name;
    });

    return {
      type: "state",
      name: state.name,
      data_point_count: state.data_point_count,
      children: stateChildren,
    };
  });

  const flatLocations = hierarchicalLocations.reduce((acc, state) => {
    // @ts-expect-error TS2345
    acc.push(state);
    // @ts-expect-error TS2345
    acc.push(...state.children.map((city) => ({ ...city, parent: state.name })));
    return acc;
  }, []);

  function getChildLocationLabel(name: string, parent: any, activeDataset: string) {
    if (activeDataset === "mercer") {
      return `${name.includes("-") ? name.split("-")[0] + " Metro Area" : name}`;
    } else if (activeDataset === "comptryx") {
      return `${locationLabelMap[name] ?? name}`;
    }
    return name;
  }

  const flatLocationSelectOptions = flatLocations.map((location, i) => ({
    // @ts-expect-error TS2339
    value: `${location.name}__${i}`,
    // @ts-expect-error TS2339
    navigationValue: location.name,
    // @ts-expect-error TS2339
    label: location.parent
      ? // @ts-expect-error TS2339
        getChildLocationLabel(location.name, location.parent, activeDataset)
      : // @ts-expect-error TS2339
        `${location.name}`,
    // @ts-expect-error TS2339
    data_point_count: location.data_point_count,
    // @ts-expect-error TS2339
    disabled: !location.data_point_count,
    // @ts-expect-error TS2339
    parent: location.parent,
    // @ts-expect-error TS2339
    type: location.type,
    // @ts-expect-error TS2339
    searchString: `${location.name} ${locationLabelMap[location.name] ?? ""} ${location.parent} ${location.children?.map((c: { name: any }) => c.name).join(" ")}`,
    // @ts-expect-error TS2339
    children: location.children,
  }));

  function renderExperimentalHierarchalGeoSelector() {
    return (
      <Stack gap={16} p={8} mt={8} mb={16}>
        <Group gap={6}>
          <Radio
            value="tier"
            checked={currentGeoFilter === "tier"}
            onChange={() => setCurrentGeoFilter("tier")}
            label="Filter by Location Tier"
          />
          <HoverCard
            openDelay={200}
            withinPortal
            width={360}
            shadow="md"
            position="right"
            zIndex={9999999}
          >
            <HoverCard.Target>
              <Box sx={{ cursor: "pointer" }}>
                <IconHelpCircle color="gray" size={16} />
              </Box>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Stack p={16} gap={16}>
                <Text size="sm">
                  <strong>Location Tiers</strong>
                </Text>
                <Text size="sm">
                  <strong>Tier 1</strong>: San Francisco Bay Area, New York City, Seattle, Los
                  Angeles
                </Text>
                <Text size="sm">
                  <strong>Tier 2</strong>: Austin, Boston, Chicago, Denver, Portland, Miami,
                  Sacramento, San Diego, Washington D.C.
                </Text>
                <Text size="sm">
                  <strong>Tier 3</strong>: Atlanta, Baltimore, Charlotte, Columbus, Dallas, Detroit,
                  Durham, Indianapolis, Las Vegas, Minneapolis, Philadelphia, Pittsburgh, Raleigh,
                  Salt Lake City, Tampa
                </Text>
                <Text size="sm">
                  <strong>Tier 4</strong>: Cincinnati, Des Moines, Houston, Kansas City,
                  Jacksonville, Louisville, Memphis, Phoenix, San Antonio, St. Louis, Tulsa
                </Text>
              </Stack>
            </HoverCard.Dropdown>
          </HoverCard>
        </Group>
        {currentGeoFilter === "tier" &&
          renderIndividualFilter(
            dimensions.find((d) => d.name === "tier"),
            "US Tier",
          )}
        <Radio
          value="location"
          checked={currentGeoFilter === "location"}
          onChange={() => setCurrentGeoFilter("location")}
          label="Filter by Location"
        />
        {currentGeoFilter === "location" && (
          <MercerComboBox
            maxDropdownHeight={600}
            comboboxProps={{ ...SharedComboboxProps, width: 400 }}
            withinPortal
            clearable={activeDataset === "mercer"}
            searchable
            data={flatLocationSelectOptions}
            filter={FilterBySearchString}
            // @ts-expect-error TS2322
            value={
              flatLocationSelectOptions.find((location) => location.navigationValue === query.city)
                ?.value ||
              flatLocationSelectOptions.find((location) => location.navigationValue === query.state)
                ?.value
            }
            placeholder="Location"
            onChange={(value) => {
              const currentlyAppliedDimensions = allDimensions.filter(
                // @ts-expect-error TS7053
                (d) => nextRouteData.current[d],
              );

              const dimensionsToClear = getDimensionsToClear(
                "city",
                currentlyAppliedDimensions,
                activeDataset,
              );

              allDimensions.map((d) => {
                // @ts-expect-error TS18048
                if (dimensionsToClear.includes(d)) {
                  // @ts-expect-error TS7053
                  nextRouteData.current[d] = undefined;
                }
              });

              const currentDimensionIsCity =
                flatLocationSelectOptions.find((location) => location.value === value)?.type ===
                "city";

              const currentDimensionNavigationValue = flatLocationSelectOptions.find(
                (location) => location.value === value,
              )?.navigationValue;

              nextRouteData.current[currentDimensionIsCity ? "city" : "state"] =
                currentDimensionNavigationValue;

              replaceQuery(nextRouteData.current);
            }}
          />
        )}
      </Stack>
    );
  }

  const unsortedSupersectors = sectors
    .filter((sector) => sector.sector === "All")
    .map((sector) => ({
      name: sector.supersector,
      data_point_count: sector.data_point_count,
    }));

  const supersectors = _.sortBy(unsortedSupersectors, "name");

  const sectorKey = DatasetDimensionsKeyMap[activeDataset].sector ?? "sector";

  const hierarchicalSectors = supersectors.map((supersector) => {
    const supersectorChildren = _.uniqBy(sectors, "sector")
      .filter((sector) => sector.supersector === supersector.name && sector.sector !== "All")
      .map((sector) => ({
        type: "sector",
        name: sector.sector,
        data_point_count: sector.data_point_count,
      }))
      .sort();
    return {
      type: "supersector",
      name: supersector.name,
      data_point_count: supersector.data_point_count,
      children: supersectorChildren,
    };
  });

  const flatSectors = hierarchicalSectors.reduce((acc, supersector) => {
    // @ts-expect-error TS2345
    acc.push(supersector);
    // @ts-expect-error TS2345
    acc.push(...supersector.children.map((sector) => ({ ...sector, parent: supersector.name })));
    return acc;
  }, []);

  const flatSectorSelectOptions = flatSectors.map((sector, i) => ({
    // @ts-expect-error TS2339
    value: `${sector.name}__${i}`,
    // @ts-expect-error TS2339
    navigationValue: sector.name,
    // @ts-expect-error TS2339
    label: sector.parent ? `${sector.name}` : `${sector.name}`,
    // @ts-expect-error TS2339
    data_point_count: sector.data_point_count,
    // @ts-expect-error TS2339
    disabled: !sector.data_point_count,
    // @ts-expect-error TS2339
    parent: sector.parent,
    // @ts-expect-error TS2339
    type: sector.type,
    // @ts-expect-error TS2339
    searchString: `${sector.name} ${sector.parent} ${sector.children?.map((c: { name: any }) => c.name).join(" ")}`,
  }));

  function renderExperimentalHierarchicalSectorSelector() {
    return (
      <Stack>
        <MercerComboBox
          maxDropdownHeight={600}
          comboboxProps={SharedComboboxProps}
          withinPortal
          searchable
          clearable
          data={flatSectorSelectOptions}
          filter={FilterBySearchString}
          placeholder="Sector"
          // @ts-expect-error TS2322
          value={
            flatSectorSelectOptions.find((sector) => sector.navigationValue === query.sector)
              ?.value ||
            flatSectorSelectOptions.find((sector) => sector.navigationValue === query.supersector)
              ?.value
          }
          onChange={(value) => {
            const currentlyAppliedDimensions = allDimensions.filter(
              (d) => (nextRouteData.current as any)[d],
            );

            const dimensionsToClear = getDimensionsToClear(
              "sector",
              currentlyAppliedDimensions,
              activeDataset,
            );

            allDimensions.map((d) => {
              // @ts-expect-error TS18048
              if (dimensionsToClear.includes(d)) {
                // @ts-expect-error TS7053
                nextRouteData.current[d] = undefined;
              }
            });

            const currentDimensionIsSector =
              flatSectorSelectOptions.find((sector) => sector.value === value)?.type === "sector";

            const currentDimensionNavigationValue = flatSectorSelectOptions.find(
              (sector) => sector.value === value,
            )?.navigationValue;

            nextRouteData.current[currentDimensionIsSector ? "sector" : "supersector"] =
              currentDimensionNavigationValue;
            replaceQuery(nextRouteData.current);
          }}
        />
      </Stack>
    );
  }

  function getQuerySectionsFromQuery(query: {
    jobFamily?: string;
    subFamily?: string;
    specialization?: string;
    subsector?: string;
    supersector: any;
    revenue: any;
    employeeCount: any;
    city: any;
    sector: any;
    state: any;
    tier: any;
    region?: string;
    level?: string;
    dimensionType?: string;
    dimensionValue?: string;
  }) {
    const sections = [];
    if (query.city || query.state || query.tier) {
      sections.push("geo");
    }
    if (query.sector || query.supersector) {
      sections.push("companyType");
    }
    if (query.employeeCount || query.revenue) {
      sections.push("companyType");
    }
    if (sections.length === 0) {
      sections.push("geo");
    }

    if (activeDataset === "comptryx") {
      if (query.revenue || query.sector) {
        sections.push("companyType");
      }
      return sections;
    } else {
      return sections[0];
    }
  }

  return (
    <Stack key={Object.values(query).join(" ")}>
      <Divider />
      <Text fw={400} size="xs" p={8} tt="uppercase">
        Filters
      </Text>
      <Accordion
        // @ts-expect-error TS2345
        defaultValue={getQuerySectionsFromQuery(query)}
        multiple={activeDataset === "comptryx" ? true : false}
      >
        <Accordion.Item value="geo">
          <Accordion.Control pb={8}>
            <SubHeader>Location</SubHeader>
          </Accordion.Control>
          <Accordion.Panel>{renderExperimentalHierarchalGeoSelector()}</Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="companyType">
          <Accordion.Control py={8}>
            <SubHeader>Company Type</SubHeader>
          </Accordion.Control>
          <Accordion.Panel>
            <Box p={8}>
              {loadingDimensions ? (
                <Skeleton height={"30px"} />
              ) : (
                <Stack gap={16}>
                  <Radio
                    value="sector"
                    checked={currentCompanyTypeFilter === "sector"}
                    onChange={() => setCurrentCompanyTypeFilter("sector")}
                    label="Filter by Sector"
                  />
                  {currentCompanyTypeFilter === "sector" &&
                    activeDataset !== "mercer" &&
                    renderIndividualFilter(
                      dimensions.find(
                        (d) => d.name === DatasetDimensionsKeyMap[activeDataset].sector,
                      ),
                    )}
                  {currentCompanyTypeFilter === "sector" &&
                    activeDataset !== "comptryx" &&
                    renderExperimentalHierarchicalSectorSelector()}
                  <Radio
                    value="revenue"
                    checked={currentCompanyTypeFilter === "revenue"}
                    onChange={() => setCurrentCompanyTypeFilter("revenue")}
                    label="Filter by Revenue"
                  />
                  {currentCompanyTypeFilter === "revenue" &&
                    renderIndividualFilter(
                      dimensions.find(
                        (d) => d.name === DatasetDimensionsKeyMap[activeDataset].revenue,
                      ),
                    )}

                  {activeDataset !== "comptryx" && (
                    <Radio
                      value="employeeCount"
                      checked={currentCompanyTypeFilter === "employeeCount"}
                      onChange={() => setCurrentCompanyTypeFilter("employeeCount")}
                      label="Filter by Employee Count"
                    />
                  )}
                  {currentCompanyTypeFilter === "employeeCount" &&
                    renderIndividualFilter(dimensions.find((d) => d.name === "employeeCount"))}
                </Stack>
              )}
            </Box>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <DataSetSection activeDataset={activeDataset} />
      <ToolsSection />
    </Stack>
  );
}

function SubHeader(props: TextProps): JSX.Element {
  // @ts-expect-error TS2322
  return <Title order={6} {...props} />;
}
